/**
 * Vilax Logo — shadows VLT's Logo component.
 *
 * Renders the Vilax 2023 wordmark as inline SVG with `fill="currentColor"`,
 * so the logo adopts the color of its parent. Paired with header CSS that
 * sets white on overlay mode and brand green on solid mode.
 *
 * Source path: Downloads/vilax_logga/Vilax2023_logo_clean.svg
 * (viewBox 0 0 998.4 494 — cursive Vilax wordmark, single path)
 *
 * DR-2026-055 §3 (Logo shadow). Milano styling pass 2026-04-17.
 */
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

const messages = defineMessages({
  home: { id: 'Home', defaultMessage: 'Home' },
  logoOf: { id: 'Logo of', defaultMessage: 'Logo of' },
});

const LOGO_PATH =
  'm377.8 175.9-58.3 19.4c-.4.1-.5.5-.4.9 19.4 45.8 24.1 48.6 17.2 67.2-4.2 11.5-14.4 34.5-24.8 55.1-39 77-72.5 113-72.5 113s44.8-178.4 25.5-258.1c-21.4-88-107.3-104.7-168.2-77.5-27.7 12.4-50.8 28.1-71.2 50.5-1.2 1.3-1.3 3.5-.2 4.8.5.6 1 1.4 1.4 2.1 1 1.6 2.9 1.7 4.3.3 25-26.1 54.8-45.8 87.6-54.7C184.5 81 219.9 122.2 226 192.3c6.3 72.3-6.9 136.3-33.8 271.8 43.5-15.5 51.9-14.7 57.5-22.1 82.2-108.9 107.6-198.2 130.2-264.1.4-1.2-.8-2.4-2-2Zm79.3 14.8c11.7-12.8 19.4-30.5 9.1-39.1-9-7.5-25-1-37.7 14.6-13 16.6-14.3 31.6-7 38.5 7.5 6.5 23.6-.3 35.6-14.1Zm519.6 27.9c-.8-1.9-3.3-2.4-4.8-.8-27.2 30.3-71.5 51.6-117 30-26.5-12.6-44.5-38.3-61.1-71.4 16.7-26.1 33.4-57.2 49.1-93.5.5-1.1-.6-2.3-1.8-1.9L795 96.3c-.3.1-.5.5-.3.8 13.1 29.8 14.9 29.4 7.4 45.3-2.8 5.9-7.3 13.1-13.8 22.8-8.4-17.7-16.6-37.2-25.6-57.6-.1-.3-.4-.4-.7-.3l-35.8 11.9c-.3.1-.5.5-.3.8 13.3 31 26.4 56.5 40.1 77.1-28.8 38.7-48.1 52.5-55.8 54.7-17.4 5-17.5-19.9-12.3-49.3 2.1-11.9 5.5-25.9 11.2-39.5.6-1.5-.8-3.1-2.4-2.6-8.3 2.8-29.7 10-32 10.8q-.15 0-.3.3c-.8 1.7-6.8 14.4-11.6 38.7-3.5 17.6-4 31.3-2.6 41.7-37.6 44.7-48 31.3-43.5-17.7 2.8-29.5 30.9-110.7 80.9-91.7 1.4.5 3 .3 4.2-.5.3-.2.3-.7 0-.9-37.5-31.9-91.9 18.9-109.5 63.6-8.6 21.7-12.2 40.2-12.8 55.2-28 46.8-71.4 81.8-63.6 11.6 1.7-3 3.5-6 5.2-9.2 51.2-93.1 70.8-128 87.4-208 4.7-22.6-7.9-59.3-40.2-16.4-41.4 55-72.7 139.9-83.6 212.3-3.5 23.1-5.3 42.3-4.6 56.9-38.4 52.5-60.4 41.3-52.1-7.6 6.4-37.6 17.3-63.8 21.8-73.4.7-1.5-.8-3.2-2.4-2.6l-49.1 16.4c-.3.1-.5.5-.4.7 6.4 14.4 7.6 15.1 3.1 23.6-24.1 45.9-52.5 94.4-67.3 114.9-.3.4.1 1 .6.9 10.3-3.4 19-10.4 24.5-19.8 33.9-57.5 42.2-72 42.2-72-15.8 44-5 73.8 6.1 78.3 12.2 5 42.2-8.8 74.5-46.6 4.4 20.3 17.8 24.7 45 8.8 14.5-8.5 32.9-26.5 53.3-53.5 2 17.2 8.4 27.2 14.2 29 12.7 3.9 39-6.2 68.2-42.2 3.9 11.8 11 16.6 17.4 16.4 22.7-.8 56.9-24.6 92.8-71.9 28.7 39.5 59.9 58.1 99.4 60.4 45.6 2.7 81.3-17.3 104.1-45.6.7-.8.8-1.9.4-2.9Zm-388.2-177c5.7-9.5 19.8-18.5 5.6 33.7-16.2 59.1-36.9 100.5-72.2 163.2 26.7-125.4 55.4-178.4 66.6-196.9';

const Logo = () => {
  const intl = useIntl();
  const navroot = useSelector((state) => state.navroot?.data?.navroot);
  const site = useSelector((state) => state.site?.data);
  const navRootPath = flattenToAppURL(navroot?.['@id']) || '/';
  const siteTitle = site?.['plone.site_title'] || 'Vilax';

  return (
    <Link to={navRootPath} aria-label={intl.formatMessage(messages.home)}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 998.4 494"
        role="img"
        aria-label={`${intl.formatMessage(messages.logoOf)} ${siteTitle}`}
      >
        <path d={LOGO_PATH} fill="currentColor" />
      </svg>
    </Link>
  );
};

export default Logo;
